// src/pages/Stickers/Stickers.jsx
//
// СТРАНИЦА «МЫ ПРЕВРАЩАЕМ ИДЕИ В СТИКЕРЫ»
//
// ─────────────────────────────────────────────────────────────────────
// 🔑 ПОЧЕМУ ТАК УСТРОЕНО (решения владельца, сессия 2026-10-28):
//
// ① НЕ МАГАЗИН ЦИФР, А МЕСТО ВДОХНОВЕНИЯ (как Pinterest).
//    Сначала слоган и лента картинок, а НЕ форма «размер/тираж».
//    Размер и тираж живут в модалке карточки — там, где рождается заказ.
//
// ② МЫ ПРОДАЁМ ПЕЧАТЬ, А НЕ КАРТИНКИ.
//    «Вы загружаете — мы печатаем»: права на изображение несёт загрузивший.
//    Цена 15 ₽ — ТОЛЬКО на карточке и в модалке, в шапке её нет.
//
// ③ НА ВИТРИНЕ — ТОЛЬКО МАКЕТ НАКЛЕЙКИ (`mockup_file`).
//    Картинка клиента (`source_file`) и печатный файл (`print_file`) на
//    страницу НЕ попадают никогда: печатный — вообще только в админке.
//
// ④ РЕКЛАМНЫЕ ФОТО (скейт/машина/стена) — НЕ в сетке товаров, а в
//    отдельном блоке «как это выглядит на вещах». Владелец: эти фото
//    нужны для рекламы; единичные изделия — отдельно.
// ─────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { stickersAPI } from '../../api/stickers.js';
import { openAuth } from '../../store/uiSlice.js';
import {
  addStickerToCart,
  uploadStickerCartImage,
} from '../../store/stickerCartSlice.js';
import { UploadOwnImage } from '../../components/stickers/UploadOwnImage/UploadOwnImage.jsx';

const PRICE_PER_PIECE = 15; // ₽ за штуку (решение владельца)
const DELIVERY_COST = 150;  // ₽ Почтой России

// Размеры стикера по длинной стороне, мм (решение владельца: 5/7/10 + свой)
const SIZES = [
  { mm: 50, label: '5 см' },
  { mm: 70, label: '7 см' },
  { mm: 100, label: '10 см' },
];

// Кромка: физически 2 мм, но не больше 10 % ширины.
// 🔑 ЗАЧЕМ ОГРАНИЧЕНИЕ: кромка 2 мм на стикере 5 см занимает 80 % ширины —
//    наклейка превращается в белую рамку (проверено на демо-снимке).
const EDGE_MM = 2;
const EDGE_MAX_SHARE = 0.1;
const edgeFor = (sizeMm) => Math.min(EDGE_MM, sizeMm * EDGE_MAX_SHARE);

// Рекламные фото владельца (frontend/public/images/stickers/).
// ⚠️ Это НЕ товары. В сетке стикеров их нет — только в блоке ниже.
const LOOKS_LIKE = [
  { src: '/images/stickers/skate-01.jpg', cap: 'Скейт' },
  { src: '/images/stickers/car-01.jpg', cap: 'Машина' },
  { src: '/images/stickers/board-01.jpg', cap: 'Витрина' },
  { src: '/images/stickers/window-01.jpg', cap: 'Стена' },
  { src: '/images/stickers/desk-01.jpg', cap: 'Стол' },
  { src: '/images/stickers/sheets-01.jpg', cap: 'Листы' },
];

// ============================================================
// СЛОТ «НЕТ КАРТИНКИ» — то, что видит владелец, пока не подставил фото
// ============================================================
const EmptySlot = ({ note }) => (
  <div className="flex h-full w-full flex-col items-center justify-center gap-1 rounded-2xl border-[1.5px] border-dashed border-[#cfcfd4] bg-surface-2 px-3 text-center">
    <span className="text-[12px] font-semibold text-text-secondary">Нет картинки</span>
    <span className="text-[10.5px] text-muted">{note}</span>
  </div>
);

// ============================================================
// «КАКИЕ СТИКЕРЫ НАПЕЧАТАТЬ?» — ТРИ ЧЕСТНЫХ ВАРИАНТА (решение владельца
// 2026-10-29). Задаётся ДО оформления, потому что мы должны знать,
// ЧТО печатать: позиция «макет ждём» — это не «ничего не нужно».
// ============================================================
const DESIGN_OPTIONS = [
  { key: 'catalog', title: 'Возьму макет из каталога', hint: null },
  { key: 'upload', title: 'Загружу свою картинку', hint: 'PNG, JPG · до 20 МБ · не серийное' },
  { key: 'pending', title: 'Ещё не решил — помогите подобрать', hint: 'Свяжемся и подскажем' },
];

// ============================================================
// МОДАЛКА КАРТОЧКИ (с размытым фоном — решение владельца)
// ============================================================
const PinModal = ({ pin, onClose }) => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((st) => st.auth);
  // ⚠️ `postLoginIntent` читаем из стора: после входа модалка авторизации
  //    закрывается, а страница должна продолжить то, что человек начал
  //    (владелец: «возвращались на страницу с которой регистрировались»).
  const postLoginIntent = useSelector((st) => st.ui.postLoginIntent);

  const [sizeMm, setSizeMm] = useState(70);
  const [qty, setQty] = useState(1);
  const [rights, setRights] = useState(false);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [err, setErr] = useState('');
  // «Загружу свою картинку» — второй режим той же модалки.
  const [mode, setMode] = useState('catalog'); // 'catalog' | 'upload' | 'pending'

  const total = qty * PRICE_PER_PIECE;
  const edge = edgeFor(sizeMm);

  // Закрытие по Esc — привычное поведение модалки.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ============================================================
  // ДОБАВЛЕНИЕ В КОРЗИНУ — ОДИН ПУТЬ ДЛЯ ВСЕХ ТРЁХ ВАРИАНТОВ
  // ============================================================
  // 🔑 ЧТО ШЛЁМ И ЧЕГО НЕ ШЛЁМ:
  //    шлём — карточку, размер, количество, отметку прав и «макет ждём»;
  //    НЕ шлём — цену. Её считает сервер (`config/stickers.js`), иначе
  //    через консоль можно было бы «договориться» о своей цене.
  // ⚠️ Права спрашиваем ВСЕГДА, в том числе для карточки каталога:
  //    клиент заказывает печать чужой картинки, и правовая модель
  //    (`STICKERS.md` §6) держится на этой отметке.
  const handleAdd = async ({ file, sizeMm: sizeArg, qty: qtyArg, pending }) => {
    setErr('');
    if (!rights) {
      setErr('Подтвердите, что права на изображение у вас есть.');
      return;
    }
    setAdding(true);
    try {
      if (pending) {
        // «Макет ждём»: позиции без файла — заказ уйдёт, печать ждёт картинку.
        const res = await dispatch(addStickerToCart({
          size_mm: sizeArg, quantity: qtyArg, rights_confirmed: true, design_status: 'pending',
        })).unwrap();
        // ⚠️ Файла нет — приносим его отдельным запросом к созданной строке.
        setAdded(true);
        onAdded(res?.cart);
        return;
      }

      if (file) {
        // Своя картинка: сначала строка корзины, потом файл к ней.
        const res = await dispatch(addStickerToCart({
          size_mm: sizeArg, quantity: qtyArg, rights_confirmed: true, design_status: 'pending',
        })).unwrap();
        await dispatch(uploadStickerCartImage({ id: res.item.id, file })).unwrap();
        setAdded(true);
        onAdded();
        return;
      }

      // Каталог.
      const res = await dispatch(addStickerToCart({
        sticker_image_id: pin.id,
        size_mm: sizeArg,
        quantity: qtyArg,
        rights_confirmed: true,
      })).unwrap();
      setAdded(true);
      onAdded(res?.cart);
    } catch (e) {
      setErr(typeof e === 'string' ? e : (e?.message || 'Не удалось добавить в корзину'));
    } finally {
      setAdding(false);
    }
  };

  const [cartTotal, setCartTotal] = useState(null);
  const onAdded = (cart) => {
    if (cart) setCartTotal(cart.total);
  };

  // 🔑 «В корзину» у гостя / регистрация поверх страницы.
  //    Гость МОЖЕТ купить без аккаунта — поэтому не запираем корзину
  //    за регистрацией. Кнопка лишь подсказывает: с аккаунтом заказ
  //    сохранится. Это осознанное отличие от прежнего поведения
  //    (ссылки вели на /register и уводили со страницы).
  const needsAuthHint = !isAuthenticated && added;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      style={{ background: 'rgba(23,25,29,0.34)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={pin.title || 'Стикер'}
    >
      <div
        className="grid w-full max-w-[860px] grid-cols-1 overflow-hidden rounded-3xl bg-surface shadow-[0_24px_60px_rgba(0,0,0,0.28)] md:grid-cols-[1.15fr_1fr]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-center bg-surface-2 p-4">
          {pin.mockup_file
            ? <img src={pin.mockup_file} alt={pin.title || 'Стикер'} className="max-h-[520px] w-auto max-w-full rounded-xl" />
            : <div className="h-[320px] w-full"><EmptySlot note="макет наклейки" /></div>}
        </div>
        <div className="flex flex-col gap-3.5 p-6">
          <div className="text-[11.5px] text-muted">
            ♡ {pin.likes_count ?? 0} · {pin.views_count ?? 0} просмотров
          </div>
          <h3 className="text-[19px] font-extrabold leading-tight tracking-[-.015em]">
            {pin.title || 'Без названия'}
          </h3>

          {mode === 'upload' ? (
            // ── РЕЖИМ «СВОЯ КАРТИНКА» ─────────────────────────────────
            <>
              <button
                type="button"
                onClick={() => { setMode('catalog'); setErr(''); }}
                className="self-start text-[11.5px] font-semibold text-text-secondary"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                ← Вернуться к карточке
              </button>
              <UploadOwnImage
                isBusy={adding}
                error={err}
                onSubmit={async ({ file, sizeMm: s2, qty: q, pending }) => {
                  setRights(true); // галочка внутри этого блока
                  // ⚠️ `handleAdd` читает `rights` из замыкания, а тут
                  //    ставим его чуть позже — передаём явно.
                  setErr('');
                  setAdding(true);
                  try {
                    const res = await dispatch(addStickerToCart({
                      size_mm: s2, quantity: q, rights_confirmed: true, design_status: 'pending',
                    })).unwrap();
                    if (file) await dispatch(uploadStickerCartImage({ id: res.item.id, file })).unwrap();
                    setAdded(true);
                    if (res?.cart) setCartTotal(res.cart.total);
                  } catch (e) {
                    setErr(typeof e === 'string' ? e : (e?.message || 'Не удалось добавить'));
                  } finally {
                    setAdding(false);
                  }
                  void pending;
                }}
              />
            </>
          ) : (
            <>
              <div>
                <div className="mb-2 text-[11px] font-bold uppercase tracking-[.08em] text-muted">Размер</div>
                <div className="flex gap-1.5">
                  {SIZES.map((s) => (
                    <button
                      key={s.mm}
                      type="button"
                      onClick={() => setSizeMm(s.mm)}
                      className="rounded-lg border px-3 py-1.5 text-[11.5px] font-semibold transition border-line text-text-secondary"
                      style={sizeMm === s.mm ? { background: 'rgb(var(--c-accent))', borderColor: 'rgb(var(--c-accent))', color: '#fff' } : undefined}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <div className="mt-1.5 text-[10.5px] text-muted">
                  Кромка по контуру: {edge.toFixed(1)} мм (2 мм, но не больше 10 % ширины)
                </div>
              </div>

              <div>
                <div className="mb-2 text-[11px] font-bold uppercase tracking-[.08em] text-muted">Количество</div>
                <div className="inline-flex overflow-hidden rounded-lg border border-line">
                  <button type="button" className="grid h-8 w-8 place-items-center text-base font-bold text-text-secondary" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="меньше">−</button>
                  <b className="grid w-12 place-items-center border-x border-line text-[12.5px]">{qty}</b>
                  <button type="button" className="grid h-8 w-8 place-items-center text-base font-bold text-text-secondary" onClick={() => setQty((q) => q + 1)} aria-label="больше">+</button>
                </div>
              </div>

              <div className="mt-1 flex items-baseline justify-between border-t border-line pt-3">
                <span className="text-[12px] font-semibold text-text-secondary">{qty} шт × {PRICE_PER_PIECE} ₽</span>
                <b className="text-2xl font-extrabold tracking-[-.02em]">{total.toLocaleString('ru-RU')} ₽</b>
              </div>

              {/* ── ЧТО ПЕЧАТАТЬ ───────────────────────────────────── */}
              <div className="flex flex-col gap-1.5">
                {DESIGN_OPTIONS.map((o) => {
                  const checked = mode === o.key;
                  return (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => { setMode(o.key); setErr(''); }}
                      className="flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition"
                      style={{
                        borderColor: checked ? 'rgb(var(--c-accent))' : 'rgb(var(--c-border))',
                        background: checked ? 'rgb(var(--c-accent-soft))' : 'rgb(var(--c-surface))',
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        style={{
                          width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                          border: checked ? '5px solid rgb(var(--c-accent))' : '1.5px solid rgb(var(--c-border))',
                          background: '#fff',
                        }}
                      />
                      <span style={{ minWidth: 0 }}>
                        <span className="block text-[12.5px] font-semibold" style={{ color: 'rgb(var(--c-text))' }}>{o.title}</span>
                        {o.hint && <span className="block text-[10.5px]" style={{ color: 'rgb(var(--c-text-secondary))' }}>{o.hint}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* ── ПРАВА ──────────────────────────────────────────── */}
              <label className="flex cursor-pointer items-start gap-2 text-[11px] leading-[1.5] text-text-secondary">
                <input type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>У меня есть права на изображение. Ответственность за права несёт тот, кто загрузил.</span>
              </label>

              {err && <div className="text-[11.5px] leading-[1.45]" style={{ color: 'rgb(var(--c-danger))' }}>{err}</div>}

              {/* ── КНОПКИ: ДО И ПОСЛЕ ─────────────────────────────── */}
              {added ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-[12.5px] font-bold" style={{ color: '#186b2f' }}>
                    <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#1e8e3e', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 11 }}>✓</span>
                    {mode === 'pending'
                      ? 'Добавлено — макет ждём'
                      : `В корзине: ${mode === 'upload' ? '' : `${qty} шт · `}${Math.round(sizeMm / 10)} см`}
                  </div>
                  <div className="flex gap-2">
                    {/* 🔴 ЦВЕТ ТЕКСТА — ИНЛАЙНОМ, А НЕ КЛАССОМ `text-white`.
                        Глобальное правило `a { color: var(--c-accent) }` в
                        `globals.css` объявлено ПОСЛЕ Tailwind и перебивает
                        утилиту: класс применялся, а текст оставался синим на
                        зелёной кнопке (поймано на снимке). Тот же класс, что
                        `bg-accent` (грабля 10-БИС): «класс есть ≠ стиль лёг». */}
                    <Link
                      to="/cart"
                      className="flex-1 rounded-lg px-4 py-2.5 text-center text-[13.5px] font-bold"
                      style={{ background: '#186b2f', color: '#fff' }}
                    >
                      Перейти в корзину
                    </Link>
                    <button
                      type="button"
                      onClick={onClose}
                      className="flex-1 rounded-lg px-4 py-2.5 text-[13.5px] font-semibold"
                      style={{ border: '1px solid rgb(var(--c-border))', background: 'rgb(var(--c-surface))', color: 'rgb(var(--c-text))', cursor: 'pointer' }}
                    >
                      Продолжить
                    </button>
                  </div>
                  {needsAuthHint && (
                    <button
                      type="button"
                      onClick={() => dispatch(openAuth({ mode: 'register', intent: 'board' }))}
                      className="text-[11px] text-muted"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Создать аккаунт, чтобы заказ и подборка сохранились
                    </button>
                  )}
                  {cartTotal != null && (
                    <div className="text-[11px] text-muted">В корзине на {cartTotal.toLocaleString('ru-RU')} ₽</div>
                  )}
                </div>
              ) : (
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    disabled={adding || mode === 'upload'}
                    onClick={() => {
                      if (mode === 'upload') { setMode('upload'); return; }
                      handleAdd({ sizeMm, qty, pending: mode === 'pending' });
                    }}
                    className="flex-1 rounded-lg px-4 py-2.5 text-[13.5px] font-semibold text-white"
                    style={{ background: 'rgb(var(--c-accent))', opacity: adding ? 0.6 : 1, cursor: adding ? 'wait' : 'pointer', border: 'none' }}
                  >
                    {adding ? 'Добавляем…' : 'В корзину'}
                  </button>
                  <button
                    type="button"
                    className="flex-1 rounded-lg px-4 py-2.5 text-[13.5px] font-semibold"
                    style={{ border: '1px solid rgb(var(--c-border))', background: 'rgb(var(--c-surface))', color: 'rgb(var(--c-text))', cursor: 'pointer' }}
                    onClick={() => dispatch(openAuth({ mode: isAuthenticated ? 'login' : 'register', intent: 'board' }))}
                  >
                    В подборку
                  </button>
                </div>
              )}
              <div className="text-[10.5px] leading-relaxed text-muted">
                Доставка Почтой России — {DELIVERY_COST} ₽ или самовывоз в Старом Осколе. Готово за 3 дня.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// СТРАНИЦА
// ============================================================
export const Stickers = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((st) => st.auth);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState(null);
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPin, setOpenPin] = useState(null);
  // «Загрузить свою картинку» — открывает модалку в режиме загрузки.
  const [uploadOpen, setUploadOpen] = useState(false);
  // Подборки (п.88) ещё не сделаны: вместо мёртвой кнопки честное сообщение.
  const [boardNotice, setBoardNotice] = useState(false);
  const [addingPage, setAddingPage] = useState(false);
  const [pageErr, setPageErr] = useState('');
  const navigate = useNavigate();
  const [err, setErr] = useState('');

  // Чипы
  useEffect(() => {
    stickersAPI.getCategories()
      .then((r) => setCategories(r.data?.data || []))
      .catch(() => setCategories([]));
  }, []);

  // Лента (при смене чипа)
  useEffect(() => {
    setLoading(true);
    setErr('');
    stickersAPI.getList(active ? { category: active } : {})
      .then((r) => setPins(r.data?.data || []))
      .catch(() => { setPins([]); setErr('Не удалось загрузить стикеры'); })
      .finally(() => setLoading(false));
  }, [active]);

  const hasPins = pins.length > 0;
  const slots = useMemo(() => Math.max(0, 8 - pins.length), [pins.length]);

  return (
    <div className="bg-bg" style={{ fontFamily: 'var(--font-stickers-body)' }}>
      <div className="mx-auto max-w-[1240px] px-4">

        {/* СЛОГАН */}
        <header className="pb-5 pt-9 text-center">
          <h1 style={{ fontFamily: 'var(--font-stickers-head)' }} className="text-[34px] font-extrabold leading-[1.06] tracking-[-.03em] sm:text-[44px]">
            Мы превращаем <span className="text-accent">идеи в стикеры</span>
          </h1>
          <p className="mx-auto mt-3 max-w-[720px] text-[14.5px] leading-relaxed text-text-secondary">
            Сохраняйте картинки, собирайте подборки — а мы превратим их в наклейки.
            Неотклеивающийся винил, резка по контуру, от одной штуки.
          </p>
          <div className="mt-4 flex justify-center gap-2.5">
            {/* 🔑 МОДАЛКИ, А НЕ ПЕРЕХОД НА /register (2026-10-29).
                Владелец: «нужно сделать так, чтобы мы возвращались на
                страницу с которой мы регистрировались, либо было бы круто,
                если бы регистрация была бы просто в модальных окнах на
                тех же страницах». Раньше обе кнопки уводили на пустую
                страницу регистрации — и человек терял витрину.
                📌 «Загрузить свою картинку» открывает РЕЖИМ загрузки в
                модалке карточки (п.89): это и есть загрузка своей
                картинки — но выбрать её можно сразу здесь, не отходя. */}
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              className="rounded-lg px-5 py-2.5 text-[13.5px] font-semibold text-white"
              style={{ background: 'rgb(var(--c-accent))', border: 'none', cursor: 'pointer' }}
            >
              Загрузить свою картинку
            </button>
            <button
              type="button"
              onClick={() => {
                if (isAuthenticated) {
                  // Подборки — следующий шаг (п.88). Пока честно говорим,
                  // ЧТО произойдёт, вместо мёртвой кнопки.
                  setBoardNotice(true);
                } else {
                  dispatch(openAuth({ mode: 'register', intent: 'board' }));
                }
              }}
              className="rounded-lg border border-line bg-surface px-5 py-2.5 text-[13.5px] font-semibold"
              style={{ cursor: 'pointer' }}
            >
              Создать подборку
            </button>
          </div>
        </header>

        {/* ЧИПЫ */}
        <div className="flex gap-2 overflow-x-auto pb-4">
          <button
            type="button"
            onClick={() => setActive(null)}
            className={`shrink-0 rounded-full border px-3.5 py-2 text-[12.5px] font-semibold transition ${
              active === null ? 'border-ink bg-ink text-white' : 'border-line bg-surface text-text-secondary'
            }`}
          >
            Все
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActive(c.slug)}
              className={`shrink-0 rounded-full border px-3.5 py-2 text-[12.5px] font-semibold transition ${
                active === c.slug ? 'border-ink bg-ink text-white' : 'border-line bg-surface text-text-secondary'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {err && <div className="mb-4 rounded-lg bg-danger-bg px-4 py-2 text-[12px]">{err}</div>}

        {/* МОЗАИКА: карточки из API + слоты под будущие */}
        <div className="columns-1 gap-3.5 sm:columns-2 md:columns-3 lg:columns-4 xl:columns-5">
          {pins.map((pin) => (
            <button
              key={pin.id}
              type="button"
              onClick={() => setOpenPin(pin)}
              className="group relative mb-3.5 block w-full break-inside-avoid overflow-hidden rounded-2xl bg-surface-2 text-left"
            >
              {pin.mockup_file
                ? <img src={pin.mockup_file} alt={pin.title || ''} className="w-full" loading="lazy" />
                : <div className="aspect-square"><EmptySlot note="единичное изделие" /></div>}
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between rounded-b-2xl bg-gradient-to-t from-black/55 to-transparent px-3 pb-2.5 pt-6 text-[11px] font-semibold text-white opacity-0 transition group-hover:opacity-100">
                <span>♡ {pin.likes_count ?? 0} · {pin.views_count ?? 0}</span>
                <span className="rounded-lg bg-ink px-2.5 py-1.5 text-[11.5px] font-bold">{PRICE_PER_PIECE} ₽</span>
              </div>
            </button>
          ))}
          {Array.from({ length: slots }).map((_, i) => (
            <div key={`slot-${i}`} className="mb-3.5 block aspect-square w-full break-inside-avoid">
              <EmptySlot note="единичное изделие" />
            </div>
          ))}
        </div>

        {!loading && !hasPins && !err && (
          <div className="py-3 text-center text-[12.5px] text-muted">
            Пока пусто — загрузите первую картинку.
          </div>
        )}

        {/* РЕКЛАМНЫЕ ФОТО: «как это выглядит на вещах» */}
        <section className="pt-10">
          <h2 style={{ fontFamily: 'var(--font-stickers-head)' }} className="mb-1 text-[24px] font-extrabold tracking-[-.02em]">
            Как это выглядит на вещах
          </h2>
          <p className="mb-4 text-[13px] text-text-secondary">
            Фотографии наших работ и клиентов.
          </p>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            {LOOKS_LIKE.map((p) => (
              <figure key={p.src} className="overflow-hidden rounded-xl bg-surface-2">
                <img src={p.src} alt={p.cap} className="aspect-square w-full object-cover" loading="lazy" />
                <figcaption className="px-2.5 py-2 text-[11.5px] font-semibold text-text-secondary">{p.cap}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* УСЛОВИЯ */}
        <section className="pt-9">
          <div className="grid grid-cols-2 overflow-hidden rounded-xl border border-line bg-line md:grid-cols-4 md:gap-px md:bg-line">
            {[
              [`${PRICE_PER_PIECE} ₽`, 'за штуку, любой размер до 10 см'],
              ['от 1 шт.', 'тираж не обязателен'],
              ['3 дня', 'изготовление'],
              [`${DELIVERY_COST} ₽`, 'Почта России · самовывоз бесплатно'],
            ].map(([b, s]) => (
              <div key={b} className="bg-surface p-4">
                <b className="block text-[15px] font-extrabold">{b}</b>
                <span className="text-[11.5px] text-text-secondary">{s}</span>
              </div>
            ))}
          </div>

          {/* ПРАВА */}
          <div className="mt-3.5 rounded-xl border border-[#cddcec] bg-accent-soft px-5 py-4 text-[12px] leading-relaxed text-[#28405c]">
            <b className="text-ink">Вы загружаете — мы печатаем.</b> Ответственность за права на изображение несёт тот, кто его загрузил.
            Мы не проверяем картинки на авторские права.{' '}
            <Link to="/contacts" className="underline underline-offset-2">Правообладателям: прислать ссылку</Link> — удалим в течение 24 часов.
          </div>
        </section>

      </div>

      {openPin && <PinModal pin={openPin} onClose={() => setOpenPin(null)} />}

      {/* ============================================================
          «ЗАГРУЗИТЬ СВОЮ КАРТИНКУ» — ПРЯМО СО СТРАНИЦЫ (п.89)
          🔑 Раньше кнопка вела на /register и НИЧЕГО не загружала.
             Теперь это честная загрузка: модалка той же формы, что в
             карточке, но без привязки к конкретному макету каталога.
          ⚠️ Позиция всегда создаётся как «своя картинка» (kind='upload'),
             даже когда файл выбран: сначала строка корзины, потом файл
             к ней — так файл не останется без владельца.
          ============================================================ */}
      {uploadOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
          style={{ background: 'rgba(23,25,29,0.34)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' }}
          onClick={() => setUploadOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Загрузить свою картинку"
        >
          <div
            className="w-full max-w-[460px] overflow-hidden rounded-3xl bg-surface p-6 shadow-[0_24px_60px_rgba(0,0,0,0.28)]"
            onClick={(e) => e.stopPropagation()}
            style={{ fontFamily: 'var(--font-stickers-body)' }}
          >
            <div className="mb-1 flex items-start justify-between gap-4">
              <h3 style={{ fontFamily: 'var(--font-stickers-head)' }} className="m-0 text-[20px] font-extrabold leading-tight tracking-[-.02em]">
                Загрузить свою картинку
              </h3>
              <button
                type="button"
                onClick={() => setUploadOpen(false)}
                aria-label="Закрыть"
                className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[13px]"
                style={{ background: 'rgb(var(--c-surface-2))', color: 'rgb(var(--c-text-secondary))', border: 'none', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
            <p className="mb-3 mt-1 text-[12px] leading-relaxed text-text-secondary">
              Вырежем по контуру, напечатаем на виниле. {PRICE_PER_PIECE} ₽ за штуку, от одной.
            </p>
            <UploadOwnImage
              isBusy={addingPage}
              error={pageErr}
              onSubmit={async ({ file, sizeMm, qty, pending }) => {
                setPageErr('');
                if (!pending && !file) {
                  setPageErr('Выберите картинку');
                  return;
                }
                setAddingPage(true);
                try {
                  const res = await dispatch(addStickerToCart({
                    size_mm: sizeMm, quantity: qty, rights_confirmed: true, design_status: 'pending',
                  })).unwrap();
                  if (file) await dispatch(uploadStickerCartImage({ id: res.item.id, file })).unwrap();
                  setUploadOpen(false);
                  // 🔑 Дальше — сразу в корзину: человек уже сказал, что
                  // хочет; оставлять его на витрине значило бы заставлять
                  // искать, куда делось добавленное.
                  navigate('/cart');
                } catch (e) {
                  setPageErr(typeof e === 'string' ? e : (e?.message || 'Не удалось добавить'));
                } finally {
                  setAddingPage(false);
                }
              }}
            />
          </div>
        </div>
      )}

      {/* ПОДБОРКИ (п.88) — ещё не сделаны. Честное сообщение вместо
          мёртвой кнопки, которая раньше вела на регистрацию. */}
      {boardNotice && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
          style={{ background: 'rgba(23,25,29,0.34)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' }}
          onClick={() => setBoardNotice(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-[400px] rounded-2xl bg-surface p-6 text-center"
            onClick={(e) => e.stopPropagation()}
            style={{ fontFamily: 'var(--font-stickers-body)' }}
          >
            <div className="mb-2 text-[13px] font-bold">Подборки скоро появятся</div>
            <div className="mb-4 text-[12.5px] leading-relaxed text-text-secondary">
              Вы уже вошли в аккаунт. Сохранение картинок в подборки — следующая задача;
              картинки, добавленные сейчас, никуда не денутся.
            </div>
            <button
              type="button"
              onClick={() => setBoardNotice(false)}
              className="w-full rounded-lg py-2.5 text-[13px] font-semibold text-white"
              style={{ background: 'rgb(var(--c-accent))', border: 'none', cursor: 'pointer' }}
            >
              Понятно
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
